import type { RoomInfo } from '@/domain/prices/rooms/types'
import { useIntl } from '@/intl'
import { PricesUseCasesContext } from '@/scopes/prices/components/context'
import { RoomNew } from '@/scopes/prices/components/RoomSelector/RoomNew'
import { roomGroupSelector, roomTypeFilterOptionsSelector } from '@/scopes/prices/components/selectors'
import { usePricesSelector } from '@/scopes/prices/infrastructure/usePricesSelector'
import { Button, RadioGroup, SpaceBetween, tk, Typography } from '@sunwebgroup/ui-components'
import { isEmpty } from 'ramda'
import * as React from 'react'
import { styled } from 'styled-components'

const Wrapper = styled(SpaceBetween)`
  background: ${tk('color_semantic_bg_subdued')};
  padding: ${tk('spacing_md')};
`

const Header = styled(SpaceBetween)`
  ${Typography} {
    padding-top: ${tk('spacing_xs')};
    padding-bottom: ${tk('spacing_xs')};
  }

  ${Button} {
    padding: 0 ${tk('spacing_xs')} 0 ${tk('spacing_xs')};
    min-height: unset;
    font-size: ${tk('font_size_xs')};
  }
`

const DistributionText = styled(Typography)`
  color: ${tk('color_semantic_text_subdued')};
`

const Line = styled.hr`
  border-top: 1px solid #d9d6d1; //TODO replace with color token when design is clear
  margin-bottom: ${tk('spacing_md')};
  margin-top: ${tk('spacing_md')} !important;
`

export function RoomGroupNew({ roomNumber }: { roomNumber: string }) {
  const intl = useIntl()

  const { rooms, selectedRoom, stock, distribution } = usePricesSelector(roomGroupSelector, roomNumber)
  const { selectRoom } = React.useContext(PricesUseCasesContext)

  const setSelectedRoom = (roomId: string | null) => {
    selectRoom(roomNumber, roomId)
  }

  const { selectedValues } = usePricesSelector(roomTypeFilterOptionsSelector)

  if (!rooms || !distribution)
    return null

  const clearSelection = () => {
    setSelectedRoom(null)
  }

  const selectedRoomInfo = rooms.find(room => room.id === selectedRoom)

  const buildDistributionText = () => {
    let text = `${intl.formatMessage(
      { id: 'prices_room_selector_room_group-distribution_adults' },
      { adults: distribution.adults },
    )}`
    if (distribution.children > 0) {
      text += `${intl.formatMessage(
        { id: 'prices_room_selector_room_group-distribution_children' },
        { children: distribution.children },
      )}`
    }
    if (distribution.babies > 0) {
      text += `${intl.formatMessage(
        { id: 'prices_room_selector_room_group-distribution_babies' },
        { babies: distribution.babies },
      )}`
    }
    return text
  }

  const filteredRooms: RoomInfo[] = []
  const otherRooms: RoomInfo[] = []

  if (!isEmpty(selectedValues)) {
    const selectedRooms = selectedValues as string[]
    rooms.forEach((room) => {
      if (stock[room.id] !== 0) {
        if (selectedRooms.includes(room.id)) {
          filteredRooms.push(room)
        }
        else {
          otherRooms.push(room)
        }
      }
    })
  }

  return (
    <Wrapper direction="y" spacing={tk('spacing_sm')} data-qa-room-selector={roomNumber}>
      <Header direction="x" alignItems="center" spacing={tk('spacing_sm')}>
        <Typography $variant="body-lg-bold">
          {intl.formatMessage({ id: 'prices_room_selector_room_group-title' }, { roomNumber })}
        </Typography>
        <DistributionText $variant="body-md-caption">{buildDistributionText()}</DistributionText>
        {selectedRoom && (
          <Button $hierarchy="tertiary" onClick={clearSelection}>
            {intl.formatMessage({ id: 'prices_room_selector_room_group-clear-selection' })}
          </Button>
        )}
      </Header>
      {selectedRoom && selectedRoomInfo
        ? (
            <RadioGroup.Root value={selectedRoom}>
              <RoomNew roomNumber={roomNumber} {...selectedRoomInfo} />
            </RadioGroup.Root>
          )
        : (
            <RadioGroup.Root value={selectedRoom}>
              {filteredRooms.length > 0
                ? (
                    <>
                      <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                        <Typography $variant="body-md">
                          {intl.formatMessage({ id: 'prices_room_selector_filtered_rooms' })}
                        </Typography>
                        <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                          {filteredRooms.map(
                            room => stock[room.id] !== 0 && <RoomNew roomNumber={roomNumber} key={room.id} id={room.id} />,
                          )}
                        </SpaceBetween>
                      </SpaceBetween>
                      <Line />
                      <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                        <Typography $variant="body-md">
                          {intl.formatMessage({ id: 'prices_room_selector_other_rooms' })}
                        </Typography>
                        <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                          {otherRooms.map(
                            room => stock[room.id] !== 0 && <RoomNew roomNumber={roomNumber} key={room.id} id={room.id} />,
                          )}
                        </SpaceBetween>
                      </SpaceBetween>
                    </>
                  )
                : (
                    <SpaceBetween direction="y" spacing={tk('spacing_xs')}>
                      {rooms.map(room => stock[room.id] !== 0
                        && <RoomNew roomNumber={roomNumber} key={room.id} id={room.id} />)}
                    </SpaceBetween>
                  )}
            </RadioGroup.Root>
          )}
    </Wrapper>
  )
}
